// ~/services/200-tracking.client.ts
import type {CartInterface, CartItemInterface} from "~~/server/types/CartInterface";
import type {ProductListInterface} from "~~/server/types/Products";
import type {OrderInterface} from "~~/server/types/OrderInterface";


import { trackMarketing } from '~/services/marketing.client';

const contents = (cart: CartInterface) => cart.products.map(item => ({
    id: String(item.product_id), quantity: Number(item.count), item_price: Number(item.price ?? 0),
}));
const cartProperties = (cart: CartInterface) => ({
    currency: useSettingsStore().settings.currency || 'EUR',
    value: cart.products.reduce((total, item) => total + Number(item.total ?? 0), 0),
    contents: contents(cart),
});
const productProperties = (product: ProductListInterface) => ({
    currency: useSettingsStore().settings.currency || 'EUR', value: Number(product.price ?? 0),
    contents: [{ id: String(product.id), quantity: 1, item_price: Number(product.price ?? 0) }],
});

export const tracking = {
    viewItem(product: ProductListInterface) {
        const google = useGoogleEvents();
        google.viewItem(product);
        const fb = useFacebookEvents();
        fb.viewItem(product, trackMarketing('ViewContent', productProperties(product)));
    },
    addToCart(cartItem: CartItemInterface) {
        const google = useGoogleEvents();
        google.addToCart(cartItem);
        const fb = useFacebookEvents();
        fb.addToCart(cartItem, trackMarketing('AddToCart', { currency: useSettingsStore().settings.currency || 'EUR', value: Number(cartItem.total ?? 0), contents: [{ id: String(cartItem.product_id), quantity: Number(cartItem.count), item_price: Number(cartItem.price ?? 0) }] }));
    },
    removeFromCart(cartItem: CartItemInterface) {
        const google = useGoogleEvents();
        google.removeFromCart(cartItem);
        const fb = useFacebookEvents();
        fb.removeFromCart(cartItem);
    },
    viewCart(cart: CartInterface) {
        const google = useGoogleEvents();
        google.viewCart(cart);
        const fb = useFacebookEvents();
        fb.viewCart(cart);
    },
    addToFavorites(product: ProductListInterface) {
        const google = useGoogleEvents();
        google.addToFavorites(product);
        const fb = useFacebookEvents();
        fb.addToFavorites(product, trackMarketing('AddToWishlist', productProperties(product)));
    },
    beginCheckout(cart:CartInterface) {
        const google = useGoogleEvents();
        google.beginCheckout(cart);
        const fb = useFacebookEvents();
        fb.beginCheckout(cart, trackMarketing('InitiateCheckout', cartProperties(cart)));
    },
    purchase(cart:CartInterface | null, order:OrderInterface) {
        if (!order.tracking?.purchase_eligible) return;
        const { consent } = useCookieConsent();
        if (!consent.value.decided || (!consent.value.analytics && !consent.value.marketing)) return;
        const settings = useSettingsStore().settings;
        const id = order.tracking.event_id;
        const once = (channel: string, pixel: string | undefined, send: () => unknown) => {
            if (!pixel) return;
            const key = `purchase:${channel}:${pixel}:${id}`;
            try { if (sessionStorage.getItem(key)) return; } catch { /* Provider event IDs still deduplicate retries. */ }
            if (send()) {
                try { sessionStorage.setItem(key, '1'); } catch { /* Storage may be unavailable. */ }
            }
        };
        const properties = {
            currency: order.tracking.purchase?.currency ?? (order.currency || 'EUR'),
            value: Number(order.tracking.purchase?.value ?? order.preorder?.original_total ?? order.total ?? 0),
            contents: order.tracking.purchase?.contents ?? (cart ? contents(cart) : []),
        };
        if (consent.value.analytics) once('google', settings.ga || settings.gtag, () => useGoogleEvents().purchase(cart, order));
        if (consent.value.marketing) {
            once('google-ads', settings.google_ads_id && settings.google_ads_purchase_label ? `${settings.google_ads_id}/${settings.google_ads_purchase_label}` : undefined, () => useGoogleEvents().purchaseAds(order));
            once('facebook', settings.facebook_pixel, () => useFacebookEvents().purchase(cart, order, id));
            once('tiktok', settings.tiktok_pixel, () => trackMarketing('Purchase', properties, id, null, 'tiktok'));
            if (order.tracking.openai) once('openai', settings.openai_pixel, () => trackMarketing('Purchase', properties, id, order.tracking?.openai, 'openai'));
        }
    },
    completePurchase(order: OrderInterface) {
        let cart: CartInterface | null = null;
        try {
            const saved = JSON.parse(sessionStorage.getItem(`pending-purchase:${order.id}`) || 'null');
            if (saved && (!saved.saved_at || Date.now() - saved.saved_at < 24 * 60 * 60 * 1000)) cart = saved;
            else sessionStorage.removeItem(`pending-purchase:${order.id}`);
        } catch { /* Storage may be unavailable. */ }
        if (!cart && (!order.tracking?.occurred_at || Date.now() / 1000 - order.tracking.occurred_at > 3600)) return;
        this.purchase(cart, cart ? order : { ...order, tracking: order.tracking ? { ...order.tracking, openai: null } : undefined });
    },
    viewItemList(products: Pick<ProductListInterface, 'id' | 'name' | 'price'>[], listId: string) {
        if (!products.length) return;
        useGoogleEvents().event('view_item_list', { item_list_id: listId, items: products.slice(0, 100).map((p, index) => ({ item_id: String(p.id), item_name: p.name, price: p.price, index })) });
    },
    selectItem(product: Pick<ProductListInterface, 'id' | 'name' | 'price'>, listId: string, index?: number) {
        useGoogleEvents().event('select_item', { item_list_id: listId, items: [{ item_id: String(product.id), item_name: product.name, price: product.price, index }] });
    },
    shippingInfo(cart: CartInterface, shippingTier: string) {
        useGoogleEvents().event('add_shipping_info', { ...cartProperties(cart), shipping_tier: shippingTier, items: contents(cart).map(p => ({ item_id: p.id, price: p.item_price, quantity: p.quantity })) });
    },
    paymentInfo(cart: CartInterface, paymentType: string) {
        useGoogleEvents().event('add_payment_info', { ...cartProperties(cart), payment_type: paymentType, items: contents(cart).map(p => ({ item_id: p.id, price: p.item_price, quantity: p.quantity })) });
        useFacebookEvents().event('AddPaymentInfo', cartProperties(cart), trackMarketing('AddPaymentInfo', cartProperties(cart)));
    },
    lead(eventId?: string) {
        useGoogleEvents().event('generate_lead');
        useFacebookEvents().event('Lead', {}, trackMarketing('Lead', {}, eventId));
    },
    registration(method: string, eventId?: string) {
        useGoogleEvents().event('sign_up', { method });
        useFacebookEvents().event('CompleteRegistration', {}, trackMarketing('CompleteRegistration', {}, eventId));
    },
    promotion(event: 'view_promotion' | 'select_promotion', id: string, name: string) {
        useGoogleEvents().event(event, { promotion_id: id, promotion_name: name });
    },
    login(method:string) {
        const google = useGoogleEvents();
        google.login(method);
        const fb = useFacebookEvents();
        fb.login(method);
    },
    search(query:string) {
        const google = useGoogleEvents();
        google.search(query);
        const fb = useFacebookEvents();
        fb.search(query, trackMarketing('Search', { search_string: query.slice(0, 200) }));
    },
    callTo(product: ProductListInterface) {
        const google = useGoogleEvents();
        google.callToAction(product);
        const fb = useFacebookEvents();
        fb.callToAction(product, trackMarketing('Contact', productProperties(product)));
    }

};
